/**
 * Listings Transformer
 *
 * Provides typed transformation utilities for converting API listing responses
 * to frontend Listing types. Uses config-driven approach for maintainability.
 */

import type {
  Listing,
  ListingAmenities,
  ListingsSearchParams,
} from "../../types/listings";
import type { QueryParamConfig } from "../utils/queryString";
import {
  buildQueryString,
  createBooleanParamConfigs,
  createStringParamConfigs,
  createNumberParamConfigs,
} from "../utils/queryString";

// ============================================
// Amenities Mapping Configuration
// ============================================

/**
 * Mapping configuration for amenities transformation.
 * Maps API snake_case keys to frontend camelCase keys.
 * Default value is used when the API doesn't provide the field.
 */
const AMENITIES_MAPPING: Array<{
  apiKey: string;
  frontendKey: keyof ListingAmenities;
  defaultValue: boolean;
}> = [
  // Climate & comfort
  {
    apiKey: "air_conditioning",
    frontendKey: "airConditioning",
    defaultValue: false,
  },
  // Laundry
  { apiKey: "washer_hookup", frontendKey: "washerHookup", defaultValue: false },
  {
    apiKey: "washer_dryer_in_unit",
    frontendKey: "washerDryerInUnit",
    defaultValue: false,
  },
  {
    apiKey: "indoor_washer_space",
    frontendKey: "indoorWasherSpace",
    defaultValue: false,
  },
  // Bathroom
  {
    apiKey: "bath_toilet_separate",
    frontendKey: "bathToiletSeparate",
    defaultValue: false,
  },
  {
    apiKey: "reheating_bath",
    frontendKey: "reheatingBath",
    defaultValue: false,
  },
  {
    apiKey: "bathroom_dryer",
    frontendKey: "bathroomDryer",
    defaultValue: false,
  },
  { apiKey: "washlet_seat", frontendKey: "washletSeat", defaultValue: false },
  {
    apiKey: "independent_vanity",
    frontendKey: "independentVanity",
    defaultValue: false,
  },
  // Kitchen
  { apiKey: "gas_stove", frontendKey: "gasStove", defaultValue: false },
  { apiKey: "ih_cooktop", frontendKey: "ihCooktop", defaultValue: false },
  {
    apiKey: "system_kitchen",
    frontendKey: "systemKitchen",
    defaultValue: false,
  },
  {
    apiKey: "counter_kitchen",
    frontendKey: "counterKitchen",
    defaultValue: false,
  },
  { apiKey: "dishwasher", frontendKey: "dishwasher", defaultValue: false },
  // Building & outdoor
  { apiKey: "balcony", frontendKey: "balcony", defaultValue: false },
  { apiKey: "flooring_wood", frontendKey: "flooringWood", defaultValue: false },
  { apiKey: "elevator", frontendKey: "elevator", defaultValue: false },
  { apiKey: "south_facing", frontendKey: "southFacing", defaultValue: false },
  { apiKey: "corner_unit", frontendKey: "cornerUnit", defaultValue: false },
  // Security
  { apiKey: "auto_lock", frontendKey: "autoLock", defaultValue: false },
  {
    apiKey: "security_camera",
    frontendKey: "securityCamera",
    defaultValue: false,
  },
  {
    apiKey: "video_intercom",
    frontendKey: "videoIntercom",
    defaultValue: false,
  },
  { apiKey: "delivery_box", frontendKey: "deliveryBox", defaultValue: false },
  // Parking
  { apiKey: "parking", frontendKey: "parking", defaultValue: false },
  {
    apiKey: "bicycle_parking",
    frontendKey: "bicycleParking",
    defaultValue: false,
  },
  {
    apiKey: "motorcycle_parking",
    frontendKey: "motorcycleParking",
    defaultValue: false,
  },
  // Utilities
  {
    apiKey: "internet_included",
    frontendKey: "internetIncluded",
    defaultValue: false,
  },
  // Contract
  { apiKey: "pets_allowed", frontendKey: "petsAllowed", defaultValue: false },
  {
    apiKey: "instruments_ok",
    frontendKey: "instrumentsOk",
    defaultValue: false,
  },
  {
    apiKey: "foreigner_friendly",
    frontendKey: "foreignerFriendly",
    defaultValue: true,
  },
  { apiKey: "soho_ok", frontendKey: "sohoOk", defaultValue: false },
];

// ============================================
// Query Parameter Configuration
// ============================================

/** String parameters that should skip "any" value */
const STRING_PARAMS_WITH_ANY = [
  "keyMoney",
  "deposit",
  "guarantorRequired",
  "buildingAge",
  "floor",
  "walkToStation",
] as const;

/** Plain string parameters (no skip value) */
const STRING_PARAMS = [
  "prefecture",
  "city",
  "ward",
  "propertyType",
  "roomLayout",
  "cursor",
] as const;

/** Number parameters */
const NUMBER_PARAMS = [
  "rentMin",
  "rentMax",
  "sizeMin",
  "sizeMax",
  "limit",
] as const;

/** Boolean amenity filter parameters */
const BOOLEAN_AMENITY_PARAMS = [
  "airConditioning",
  "washerHookup",
  "washerDryerInUnit",
  "bathToiletSeparate",
  "balcony",
  "flooringWood",
  "internetIncluded",
  "gasStove",
  "ihCooktop",
  "elevator",
  "autoLock",
  "deliveryBox",
  "parking",
  "bicycleParking",
] as const;

/** Boolean contract filter parameters */
const BOOLEAN_CONTRACT_PARAMS = [
  "foreignerFriendly",
  "petsAllowed",
  "instrumentsOK",
  "noGuarantorNeeded",
  "moveInWithin30Days",
] as const;

/** Special boolean parameter */
const INCLUDE_MANAGEMENT_FEE_PARAM = "includeManagementFee";

/**
 * Complete query parameter configuration for listings search
 */
export const LISTINGS_QUERY_CONFIG: QueryParamConfig[] = [
  ...createStringParamConfigs(STRING_PARAMS),
  ...createStringParamConfigs(STRING_PARAMS_WITH_ANY, "any"),
  ...createNumberParamConfigs(NUMBER_PARAMS),
  ...createBooleanParamConfigs(BOOLEAN_AMENITY_PARAMS),
  ...createBooleanParamConfigs(BOOLEAN_CONTRACT_PARAMS),
  { key: INCLUDE_MANAGEMENT_FEE_PARAM, type: "boolean", onlyIfTrue: false },
];

/**
 * All boolean filter keys for URL parsing
 */
export const BOOLEAN_FILTER_KEYS = [
  ...BOOLEAN_AMENITY_PARAMS,
  ...BOOLEAN_CONTRACT_PARAMS,
] as const;

// ============================================
// Transformation Functions
// ============================================

/**
 * Transform API amenities object to frontend ListingAmenities
 *
 * @param apiAmenities - Raw amenities object from API (snake_case keys)
 * @returns Typed ListingAmenities object
 */
export function transformAmenities(
  apiAmenities: Record<string, boolean> | undefined | null,
): ListingAmenities {
  const result = {} as ListingAmenities;

  for (const { apiKey, frontendKey, defaultValue } of AMENITIES_MAPPING) {
    result[frontendKey] = apiAmenities?.[apiKey] ?? defaultValue;
  }

  return result;
}

/**
 * Raw API listing response type (snake_case)
 */
export interface ApiListingData {
  id: string;
  org_id: string;
  title: string;
  title_ja?: string;
  description?: string;
  property_type: string;
  room_layout: string;
  area_sqm: number;
  floor?: number;
  total_floors?: number;
  year_built?: number;
  price: number;
  management_fee?: number;
  deposit?: number;
  key_money?: number;
  guarantor_required?: boolean;
  address?: string;
  prefecture?: string;
  city?: string;
  ward?: string;
  station?: string;
  walk_minutes?: number;
  geo?: { lat: number; lng: number };
  amenities?: Record<string, boolean>;
  images?: string[];
  thumbnail?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Transform a single API listing to frontend Listing type
 *
 * @param data - Raw listing data from API
 * @returns Typed Listing object
 */
export function transformListing(data: ApiListingData): Listing {
  return {
    id: data.id,
    orgId: data.org_id,
    title: data.title,
    titleJa: data.title_ja,
    description: data.description ?? "",
    propertyType: data.property_type as Listing["propertyType"],
    roomLayout: data.room_layout as Listing["roomLayout"],
    areaSqm: data.area_sqm,
    floor: data.floor,
    totalFloors: data.total_floors,
    yearBuilt: data.year_built,
    price: data.price,
    managementFee: data.management_fee,
    deposit: data.deposit,
    keyMoney: data.key_money,
    guarantorRequired: data.guarantor_required,
    address: data.address ?? "",
    prefecture: data.prefecture ?? "",
    city: data.city ?? "",
    ward: data.ward,
    station: data.station,
    walkMinutes: data.walk_minutes,
    geo: data.geo,
    amenities: transformAmenities(data.amenities),
    images: data.images ?? [],
    thumbnail: data.thumbnail,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
}

/**
 * Transform array of API listings to frontend Listing array
 *
 * @param listings - Array of raw listing data
 * @returns Array of typed Listing objects
 */
export function transformListings(listings: ApiListingData[]): Listing[] {
  return listings.map(transformListing);
}

/**
 * Build query string for listings search API
 *
 * @param params - Search parameters
 * @returns URL-encoded query string
 */
export function buildListingsQueryString(params: ListingsSearchParams): string {
  return buildQueryString(
    params as Record<string, unknown>,
    LISTINGS_QUERY_CONFIG,
  );
}
